/**
 * Hook for fetching trigger preview data
 *
 * Returns the email or CRM event that triggered a proposal, for display in compact preview
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { ProposalDetail, TriggerData } from '../core/types';
import { triggerQuery } from '../query/queries';

/**
 * The two action ids the trigger query needs: what to fetch, and what to cache
 * it under. They are deliberately different.
 *
 * `update_payload` only edits an action in place for two minutes
 * (`last_payload_update_at`); after that — and on every agent revision — a save
 * mints a NEW action row with a NEW id for what is logically the same action.
 * Keying the trigger query on that id meant the key changed mid-edit, hit a
 * cold cache entry, and flipped the trigger card back to its loading state:
 * the card visibly unloaded and reloaded, reflowing the email editor rendered
 * below it while the user was typing in it.
 *
 * - `cacheActionId` is `parent_action`, which points at the ROOT version (never
 *   the immediate parent) and so is constant for the life of the logical
 *   action. Version 1 has no parent, so its own id IS the root id.
 * - `fetchActionId` stays the CURRENT version's id, because the action-detail
 *   endpoint only resolves the current version — requesting a superseded id
 *   404s, which would blank the trigger card permanently instead of just
 *   flickering it.
 *
 * The trigger is the source email/event that caused the proposal, which is the
 * same for every version of the action, so caching it under the root id is
 * correct rather than merely convenient.
 *
 * Both are 0 when there is no action, which callers use to disable the query.
 */
export function getTriggerActionIds(proposal: ProposalDetail): {
  fetchActionId: number;
  cacheActionId: number;
} {
  const primaryAction = proposal.actions[0];
  const fetchActionId = primaryAction?.id ?? 0;
  return {
    fetchActionId,
    cacheActionId: primaryAction?.parent_action ?? fetchActionId,
  };
}

interface UseTriggerPreviewOptions {
  /** Whether to enable the query (default: true) */
  enabled?: boolean;
  /**
   * Action id to cache under, when it differs from the one being fetched.
   * Defaults to `actionId`. See {@link getTriggerActionIds} for why the two
   * come apart — pass `cacheActionId` from there rather than deriving it at
   * the call site.
   */
  cacheActionId?: number;
}

interface UseTriggerPreviewResult {
  /** Trigger data array (email or CRM events), empty array if none, null on error/initial load */
  data: TriggerData[] | null | undefined;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether the query encountered an error */
  isError: boolean;
  /** Error object if any */
  error: Error | null;
  /** Whether a fetch is in progress (even with cached data) */
  isFetching: boolean;
  /**
   * Whether `data` is placeholder data carried over from a previously-viewed
   * query (via `keepPreviousData`) rather than the current query's result.
   * True while a proposal switch is in flight — callers deriving UI from
   * `data` must treat it as not-yet-loaded to avoid showing the prior
   * proposal's triggers.
   */
  isPlaceholderData: boolean;
}

/**
 * Hook for fetching trigger email preview data
 *
 * @param orgId - Organization ID
 * @param proposalId - Proposal ID
 * @param actionId - Action ID (the gmail_send action)
 * @param options - Query options
 * @returns Query result with trigger data
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useTriggerPreview(orgId, proposalId, actionId);
 *
 * if (isLoading) return <LoadingSkeleton />;
 * if (!data) return null; // No trigger or error - hide component
 *
 * return (
 *   <div>
 *     <span>{data.email.from}</span>
 *     <span>{data.email.subject}</span>
 *   </div>
 * );
 * ```
 */
export function useTriggerPreview(
  orgId: number,
  proposalId: number,
  actionId: number,
  options: UseTriggerPreviewOptions = {}
): UseTriggerPreviewResult {
  const { enabled = true, cacheActionId = actionId } = options;

  const query = useQuery({
    ...triggerQuery(orgId, proposalId, actionId, cacheActionId),
    enabled: enabled && actionId > 0,
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
  };
}
